/**
 * The register's shared cells (SA-462, spec §8.3 / D12): the words for a
 * step, a quote and a status are the same on the built-in register and on a
 * module's view (the Compass pricing view), so they live here, not in either
 * table. Pure render helpers; no hooks. The words (`statusDerivation`,
 * `words`, `STATUS_SOURCE_TITLE`) live in `registerCellText.ts` beside this
 * file, so it exports components only (SA-464 e).
 */
import { outcomeWords } from '@/components/process/stepLiveText'
import { Badge } from '@/components/ui'
import { formatCurrency } from '@/utils/currency'
import type { Order, OrderOwner } from '@/types/commerce'
import { ClampedText } from './ClampedText'
import { registerChip } from './waitingResponses'
import { INTAKE_SLOT, intakeSlot, intakeValue } from './intakeSnapshot'
import { UNKNOWN_PERSON } from './ownerText'
import { STATUS_SOURCE_TITLE, statusDerivation, words } from './registerCellText'
import { TONE_BADGE, TONE_DOT, statusTone } from './tones'
import { isWindow, windowDates, windowState, windowWords } from './window'
import { GUESSED, GUESSED_SHORT, windowLine } from './windowText'

export const Dash = () => <span className="text-muted-foreground">—</span>

/** SA-1035: the record's owner by name; the basis words are the strip's. */
export function OwnerCell({ owner }: { owner: OrderOwner | null | undefined }) {
  if (!owner) return <Dash />
  if (!owner.name) return <span className="text-muted-foreground">{UNKNOWN_PERSON}</span>
  return <ClampedText text={owner.name} />
}

export function StepCell({ order }: { order: Order }) {
  const step = order.process?.currentStep
  if (step) {
    const process = order.process!
    return (
      <div>
        <p className="text-foreground">{step.number} · {step.title}</p>
        <p className="text-[11.5px] text-muted-foreground">
          {step.seatLabel ?? step.seat ?? ''}
          {process.overdue
            ? <span className="text-destructive">{step.seatLabel || step.seat ? ' · ' : ''}overdue</span>
            : process.dueAt && <span>{step.seatLabel || step.seat ? ' · ' : ''}due {new Date(process.dueAt).toLocaleDateString()}</span>}
        </p>
      </div>
    )
  }
  // No running step: a closed record reads as its outcome, never its last step.
  if (order.outcome) return <span className="text-muted-foreground">Closed · {outcomeWords(order.outcome)}</span>
  if (order.process) return <span className="text-muted-foreground">Closed</span>
  return <span className="text-muted-foreground">Not started</span>
}

export function QuoteCell({ order }: { order: Order }) {
  const quote = order.quote
  if (!quote) return <Dash />
  const detail = quote.expired
    ? <span className="text-destructive">Expired</span>
    : quote.status === 'sent'
      ? <span>valid until {new Date(quote.validUntil).toLocaleDateString()}</span>
      : <span>{words(quote.status)}</span>
  return (
    <div>
      <p className="font-medium text-foreground">{formatCurrency(quote.amountCents, quote.currency)}</p>
      <p className="text-[11.5px] text-muted-foreground">v{quote.versionNumber} · {detail}</p>
    </div>
  )
}

/** A dotted chip over the derivation line, in the status's tone (SA-468:
 *  the pack's, else derived info / override-only warning — the STATUS
 *  filter dot reads the same tone). */
export function StatusCell({ order }: { order: Order }) {
  const status = order.commercialStatus
  if (!status) return <Dash />
  const tone = statusTone(status)
  return (
    <div className="whitespace-nowrap">
      <Badge variant={TONE_BADGE[tone]} dot={TONE_DOT[tone]} title={STATUS_SOURCE_TITLE[status.source]} className="gap-1.5">
        {status.label}
      </Badge>
      <span className="block pl-3 text-[10.5px] text-muted-foreground">{statusDerivation(status, order)}</span>
    </div>
  )
}

/** SA-1029 (spec §D24): a record with a customer update waiting, on the
 * built-in register and the Compass pricing view alike. Reads the order's
 * `customerUpdate` summary (the list's `include=updates`); nothing fetched. */
export function UpdateChip({ order }: { order: Order }) {
  const summary = order.customerUpdate
  const chip = summary ? registerChip(summary) : null
  if (!chip) return null
  // Its own block UNDER the number (spec D24): on the number's nowrap line a
  // 200px Reference cell would clip it.
  return (
    <div data-slot="update-chip" className="mt-1">
      <Badge variant="warning-soft" dot="bg-warning" title={chip.title} className="gap-1.5">
        {chip.label}
      </Badge>
    </div>
  )
}

/** SA-1141: the needed window as dates, the range and the flexibility when stated, the customer's
 *  words in the native `title` (register cells keep `title`, never a Tip, SA-1034), and a guess
 *  marked under it. An undated window or a string from before SA-1136 reads as its words
 *  (`intakeSlot`), nothing as the dash. The sort key is `needed_window` (its dates, in SQL). */
export function WindowCell({ order }: { order: Order }) {
  const value = intakeValue(order, INTAKE_SLOT.neededWindow)
  if (isWindow(value) && windowDates(value)) {
    return (
      <div className="flex flex-col items-start gap-1">
        <span className="line-clamp-2 break-words" title={windowWords(value) || undefined}>{windowLine(value)}</span>
        {windowState(value) === 'guessed' && (
          <Badge variant="muted-soft" dot="bg-muted-foreground" title={GUESSED} className="gap-1.5">{GUESSED_SHORT}</Badge>
        )}
      </div>
    )
  }
  const written = intakeSlot(order, INTAKE_SLOT.neededWindow)
  return written ? <ClampedText text={written} /> : <Dash />
}
